import {
  SOURCE_ADMIN_SMS,
  SOURCE_TCAPI,
  type LocationSources,
} from '@rsos/constants/locationSources';
import { ConversationStatus } from '@rsos/constants/streamWs/conversation';
import { User } from '@rsos/utils/useTalkJS';
import type { TCAPISessionResponse, TCAPISessionTransfer } from './tcapiAPI';

export type ConversationLoadingRecord = Record<
  'createConversation' | 'getConversationMetadata',
  boolean
>;

export type TransferStatus = 'pending' | 'accepted' | 'timed_out';

/**
 * Latest TCAPI soft-transfer state. Field shape comes from the
 * ``TCAPISessionTransferSchema`` zod schema in ``tcapiAPI.tsx``, which
 * mirrors the pydantic ``TransferState`` model on the backend.
 */
export type ConversationTransfer = TCAPISessionTransfer;

interface baseConversation {
  loading: ConversationLoadingRecord;
  source: LocationSources;
  data: unknown;
  isUnread: boolean;
  recentMessageType: ConversationStatus;
  /** Present when a TCAPI soft transfer (conference) is in progress. Set when a conversation.transfer message arrives via the communicator exchange. */
  transfer?: ConversationTransfer;
  participants: User[];
}

interface errorConversation extends baseConversation {
  data: null;
}

/**
 * Shape of a TCAPI session as returned by the TCAPI session GET endpoint.
 * Field shape comes from the ``TCAPISessionResponseSchema`` zod schema in
 * ``tcapiAPI.tsx``, which mirrors the pydantic ``SessionResponse`` model
 * on the backend.
 */
export type TCAPIConversationData = TCAPISessionResponse;

interface conversationTCAPIBase extends baseConversation {
  source: typeof SOURCE_TCAPI;
}

/** Conversation metadata has been fetched from the TCAPI session endpoint. */
interface conversationTCAPILoaded extends conversationTCAPIBase {
  data: TCAPIConversationData;
}

/** No TCAPI session metadata has been fetched for this caller yet. The matchPending extraReducer creates the entry in this shape on the first fetch, and preserves already-fetched data on later ones. */
interface conversationTCAPIPending extends conversationTCAPIBase {
  data: null;
}

export type conversationTCAPI =
  | conversationTCAPILoaded
  | conversationTCAPIPending;

/**
 * Type guard: narrows to a TCAPI conversation (loaded or pending).
 */
export function isTCAPIConversation(
  conversation: conversationStateValues,
): conversation is conversationTCAPI {
  return conversation.source === SOURCE_TCAPI;
}

/**
 * Type guard: narrows to a conversation with loaded TCAPI metadata. Note that conversations from other sources will not pass this check, even if they have a data field with similar properties to TCAPI conversations.
 */
export function isTCAPIConversationLoaded(
  conversation: conversationStateValues,
): conversation is conversationTCAPILoaded {
  return isTCAPIConversation(conversation) && conversation.data !== null;
}

interface conversationADMINSMS extends baseConversation {
  source: typeof SOURCE_ADMIN_SMS;
  data: {
    extra_info: unknown;
    created_time: number;
    source: 'admin_sms';
    application: 'text_to_911';
    caller_id: string;
    conversation_start_time: number;
    valid_for_psap: boolean;
    conversation_id: string;
    status?: string;
    last_updated_time?: number;
  };
}

export type conversationStateValues =
  | conversationADMINSMS
  | conversationTCAPI
  | errorConversation;
